import React, { useEffect, useRef } from 'react';
import { hasFinePointer, shouldAnimate } from '../../utils/motion';

const LINK_DISTANCE = 120;
const POINTER_DISTANCE = 170;

/**
 * Ambient "data network" behind the hero: slow nodes that link to each other
 * and to the cursor. Pauses when off screen; static with reduced motion.
 */
export default function HeroNetwork() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const animate = shouldAnimate();
    let width = 0;
    let height = 0;
    let points = [];
    let pointer = null;
    let running = false;
    let frame = 0;

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.round(Math.min(56, (width * height) / 15000));
      points = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      points.forEach((a, i) => {
        if (animate) {
          a.x += a.vx;
          a.y += a.vy;
          if (a.x < 0 || a.x > width) a.vx *= -1;
          if (a.y < 0 || a.y > height) a.vy *= -1;
        }
        for (let j = i + 1; j < points.length; j += 1) {
          const b = points[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < LINK_DISTANCE) {
            ctx.strokeStyle = `rgba(15, 106, 114, ${(1 - d / LINK_DISTANCE) * 0.22})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
        if (pointer) {
          const d = Math.hypot(a.x - pointer.x, a.y - pointer.y);
          if (d < POINTER_DISTANCE) {
            ctx.strokeStyle = `rgba(234, 122, 25, ${(1 - d / POINTER_DISTANCE) * 0.45})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = 'rgba(15, 106, 114, 0.45)';
        ctx.beginPath();
        ctx.arc(a.x, a.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
      });
      if (running) frame = window.requestAnimationFrame(draw);
    };

    const start = () => {
      if (running || !animate) return;
      running = true;
      frame = window.requestAnimationFrame(draw);
    };

    const stop = () => {
      running = false;
      window.cancelAnimationFrame(frame);
    };

    resize();
    draw();

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()))
      : null;
    if (observer) observer.observe(host);
    else start();

    const handleResize = () => {
      resize();
      if (!running) draw();
    };
    const handleMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };
    const handleLeave = () => {
      pointer = null;
    };

    window.addEventListener('resize', handleResize);
    const trackPointer = animate && hasFinePointer();
    if (trackPointer) {
      host.addEventListener('pointermove', handleMove);
      host.addEventListener('pointerleave', handleLeave);
    }

    return () => {
      stop();
      observer?.disconnect();
      window.removeEventListener('resize', handleResize);
      host.removeEventListener('pointermove', handleMove);
      host.removeEventListener('pointerleave', handleLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="home-hero-network" aria-hidden="true" />;
}
